import { useMemo, useState } from "react";
import { addDays, format, isSameDay, startOfDay } from "date-fns";
import { ptBR } from "date-fns/locale";
import { ArrowDownToLine, ArrowUpFromLine, CalendarClock } from "lucide-react";
import { Card, Skeleton } from "@bookdental/ui";
import { trpc } from "@/lib/trpc";
import { STATUS_META } from "@/components/prosthesis/prosthesis-meta";
import { PatientSummaryDialog, type PatientSummaryTarget } from "@/components/shared/PatientSummaryDialog";
import { prazoEfetivo, type PrazoDoLaboratorio } from "@bookdental/schemas";

type OwnedOrder = {
  id: string;
  patientId: string | null;
  patientName: string;
  status: keyof typeof STATUS_META;
  deadline: string | Date | null;
  createdAt: string | Date;
  direction: "outgoing" | "incoming";
  counterpartName: string;
  organization: { id: string; name: string };
  prazo: PrazoDoLaboratorio;
};

type Bucket = "atrasado" | "hoje" | "proximos7" | "proximos30";

const BUCKETS: { key: Bucket; label: string; accent: string }[] = [
  { key: "atrasado", label: "Atrasado", accent: "#ef4444" },
  { key: "hoje", label: "Hoje", accent: "#2457ff" },
  { key: "proximos7", label: "Próximos 7 dias", accent: "#0ea5e9" },
  { key: "proximos30", label: "Próximos 30 dias", accent: "#f59e0b" },
];

const TERMINAL = new Set(["CONCLUIDO", "CANCELADO", "RECUSADO"]);
const MAX_PER_BUCKET = 6;

/** Mesma lógica de janela rolante de @/lib/dashboard-lab (serviceDeadlineBucket), mas com o
 *  prazo do laboratório específico de cada pedido (não um único global). */
function bucketOf(o: OwnedOrder): Bucket | null {
  if (o.status === "AGUARDANDO_CONFIRMACAO" || TERMINAL.has(o.status)) return null;
  const today = startOfDay(new Date());
  const eff = prazoEfetivo(o, o.prazo);
  if (eff < today) return "atrasado";
  if (isSameDay(eff, today)) return "hoje";
  if (eff <= addDays(today, 7)) return "proximos7";
  if (eff <= addDays(today, 30)) return "proximos30";
  return null;
}

/** Resumo por prazo agregado (enviados + recebidos) das organizações onde o usuário é OWNER —
 *  versão da organização Pessoal do LabDeadlineSummary, por pedido (não por serviço). */
export function OwnedOrdersDeadlineSummary() {
  const query = trpc.prosthesis.listMineDeadlineSummaryAcrossOrgs.useQuery();
  const orders = useMemo(() => (query.data ?? []) as OwnedOrder[], [query.data]);
  const [summaryTarget, setSummaryTarget] = useState<PatientSummaryTarget | null>(null);

  const grouped = useMemo(
    () =>
      BUCKETS.map((b) => {
        const rows = orders.filter((o) => bucketOf(o) === b.key);
        return { ...b, rows: rows.slice(0, MAX_PER_BUCKET), total: rows.length };
      }).filter((b) => b.total > 0),
    [orders],
  );

  return (
    <Card className="p-5 sm:p-6">
      <div className="flex items-center gap-2.5">
        <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand-50 text-brand-700 dark:bg-primary/15 dark:text-primary">
          <CalendarClock className="h-5 w-5" />
        </span>
        <div>
          <h2 className="text-[15px] font-semibold text-zinc-900 dark:text-foreground">
            Resumo por prazo
          </h2>
          <p className="text-xs text-zinc-500 dark:text-muted-foreground">
            Pedidos enviados e recebidos das organizações onde você é dono
          </p>
        </div>
      </div>

      <div className="mt-5">
        {query.isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-5 w-32" />
            <Skeleton className="h-24 w-full" />
          </div>
        ) : grouped.length === 0 ? (
          <p className="py-6 text-center text-sm text-zinc-500 dark:text-muted-foreground">
            Nenhum pedido em aberto no momento.
          </p>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {grouped.map((bucket) => (
              <div key={bucket.key} className="rounded-xl border border-zinc-200 p-3.5 dark:border-border">
                <p
                  className="mb-2 flex items-center justify-center gap-1.5 text-xs font-semibold uppercase tracking-wide"
                  style={{ color: bucket.accent }}
                >
                  {bucket.label}
                  <span className="rounded-full bg-zinc-100 px-1.5 py-0.5 text-[10px] font-bold text-zinc-600 dark:bg-muted dark:text-muted-foreground">
                    {bucket.total}
                  </span>
                </p>
                <div className="space-y-1.5">
                  {bucket.rows.map((o) => (
                    <button
                      key={o.id}
                      type="button"
                      onClick={() =>
                        o.patientId &&
                        setSummaryTarget({
                          patientId: o.patientId,
                          organizationId: o.organization.id,
                          patientName: o.patientName,
                        })
                      }
                      disabled={!o.patientId}
                      className="block w-full rounded-lg border border-zinc-200 bg-white p-2.5 text-left transition-all hover:border-zinc-300 hover:shadow-sm disabled:cursor-default disabled:hover:border-zinc-200 disabled:hover:shadow-none dark:border-border dark:bg-card"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <p className="min-w-0 truncate text-sm font-medium text-zinc-800 dark:text-foreground">
                          {o.patientName}
                        </p>
                        {o.direction === "outgoing" ? (
                          <ArrowUpFromLine className="h-3 w-3 shrink-0 text-amber-500" />
                        ) : (
                          <ArrowDownToLine className="h-3 w-3 shrink-0 text-cyan-500" />
                        )}
                      </div>
                      <div className="mt-0.5 flex items-center justify-between gap-2">
                        <span className="min-w-0 truncate text-xs text-zinc-500 dark:text-muted-foreground">
                          {o.counterpartName}
                        </span>
                        <span className="shrink-0 text-xs text-zinc-400 dark:text-muted-foreground">
                          {o.deadline ? format(new Date(o.deadline), "dd/MM", { locale: ptBR }) : "—"}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <PatientSummaryDialog target={summaryTarget} onClose={() => setSummaryTarget(null)} />
    </Card>
  );
}
